import { useState, useRef, useEffect } from 'react';
import { useAuthContext } from '../../features/auth/context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import Portal from '../shared/Portal';
import { useNavigate, Link } from 'react-router-dom';
import { FaQuestionCircle } from 'react-icons/fa';

/**
 * Navigation Bar Component
 * 🧭 Top navigation with user menu and main actions
 */
const Navbar = ({ onOpenTour }) => {
  const { user, logout } = useAuthContext();
  const { themeColors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position
  const getDropdownPosition = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    return {
      top: `${rect.bottom + window.scrollY + 8}px`,
      right: `${window.innerWidth - rect.right}px`,
    };
  };

  return (
    <nav 
      className="border-b border-[var(--border-primary)]/50 h-16 shadow-sm bg-white"
      style={{
        // Ensure solid white background - no transparency
        backgroundColor: '#FFFFFF !important',
        // Optional: Subtle gradient overlay (commented out to ensure solid white)
        // backgroundImage: `linear-gradient(to bottom right, ${themeColors.primary50}, ${themeColors.primary100})`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="cursor-pointer">
                <h1 
                  className="text-2xl font-bold transition-all duration-500"
                  style={{
                    background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  MnyMny
                </h1>
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Help/Tour Button */}
            {onOpenTour && (
              <button
                onClick={onOpenTour}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Take a tour of MnyMny"
                aria-label="Open welcome tour"
              >
                <FaQuestionCircle 
                  className="w-6 h-6" 
                  style={{ color: themeColors.primary }}
                />
              </button>
            )}
            
            <div className="ml-3 relative">
              <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-3 px-4 py-2 rounded-lg bg-white hover:bg-gray-50 shadow-sm hover:shadow transition-all duration-200 border border-[var(--border-primary)]/30"
                style={{ backgroundColor: 'var(--bg-primary, #FFFFFF)' }}
              >
                <span 
                  className="font-medium"
                  style={{
                    background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {user?.firstName} {user?.lastName}
                </span>
                <svg
                  className={`h-5 w-5 transform transition-transform duration-200 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                  style={{ color: themeColors.primary }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown menu */}
              {isOpen && (
                <Portal>
                  <div
                    ref={dropdownRef}
                    style={{
                      ...getDropdownPosition(),
                      // Hardcoded fallback: never let the dropdown go transparent
                      // even if theme CSS variables are missing
                      backgroundColor: 'var(--bg-primary, #FFFFFF)',
                    }}
                    className="fixed w-56 rounded-lg shadow-xl ring-1 ring-black/5 z-[9999] overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-[var(--border-primary)]/50">
                      <p className="text-sm text-[var(--text-secondary)]">Signed in as</p>
                      <p 
                        className="text-sm font-medium truncate"
                        style={{
                          background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        {user?.email}
                      </p>
                    </div>
                    <button
                      onClick={() => navigate('/profile')}
                      className="w-full text-left px-4 py-3 text-sm text-[var(--text-primary)] transition-all duration-200 flex items-center space-x-2 font-medium"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themeColors.primary50;
                        e.currentTarget.style.color = themeColors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-primary, #1F2937)';
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={logout}
                      className="w-full text-left px-4 py-3 text-sm text-[var(--text-secondary)] transition-all duration-200 flex items-center space-x-2"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = themeColors.primary50;
                        e.currentTarget.style.color = themeColors.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Sign out</span>
                    </button>
                  </div>
                </Portal>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 